// src/components/POSConnect.js
// ─────────────────────────────────────────────────────────────
//  Full POS connection management UI
//  Add to frontend/src/components/
//  Used inside VendorPage.js
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import {
  getPOSTypes, getConnection, saveConnection,
  testConnection, deleteConnection,
  syncMenu, syncCSV, getSyncLogs, getWebhookUrl,
} from '../services/pos';

const POS_LABELS = {
  FOODICS:         { label: 'Foodics',          icon: '🍔', region: 'UAE / GCC / Saudi' },
  SQUARE:          { label: 'Square POS',        icon: '⬛', region: 'Global' },
  TOAST:           { label: 'Toast POS',         icon: '🍞', region: 'Global' },
  LIGHTSPEED:      { label: 'Lightspeed',        icon: '⚡', region: 'Global' },
  REVEL:           { label: 'Revel Systems',     icon: '🔄', region: 'Global' },
  CLOVER:          { label: 'Clover',            icon: '🍀', region: 'Global' },
  PETPOOJA:        { label: 'Pet Pooja',          icon: '🍛', region: 'India' },
  GENERIC_WEBHOOK: { label: 'Custom / Generic',  icon: '🔗', region: 'Any system' },
  MANUAL_CSV:      { label: 'CSV Upload',        icon: '📄', region: 'No POS needed' },
};

export default function POSConnect() {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [step,       setStep]       = useState('overview'); // overview|select|configure|connected
  const [types,      setTypes]      = useState([]);
  const [connection, setConnection] = useState(null);
  const [selected,   setSelected]   = useState('');
  const [form,       setForm]       = useState({ apiKey: '', apiSecret: '', accessToken: '', apiEndpoint: '', webhookSecret: '', locationId: '', syncMode: 'WEBHOOK' });
  const [logs,       setLogs]       = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [status,     setStatus]     = useState({ loading: false, msg: '', error: '' });

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const [typesRes, connRes] = await Promise.all([getPOSTypes(), getConnection()]);
      setTypes(typesRes.data?.types || []);
      if (connRes.data?.connected) {
        setConnection(connRes.data.connection);
        setStep('connected');
        loadLogs();
        loadWebhookUrl();
      }
    } catch (_) {}
  };

  const loadLogs = async () => {
    try { const r = await getSyncLogs(); setLogs(r.data || []); } catch (_) {}
  };

  const loadWebhookUrl = async () => {
    try { const r = await getWebhookUrl(); setWebhookUrl(r.data?.webhookUrl || ''); } catch (_) {}
  };

  const handleSave = async () => {
    setStatus({ loading: true, msg: '', error: '' });
    try {
      await saveConnection({ posType: selected, ...form });
      setStatus({ loading: false, msg: 'Connection saved!', error: '' });
      setTimeout(() => init(), 800);
    } catch (err) {
      setStatus({ loading: false, msg: '', error: err.response?.data?.error || 'Save failed' });
    }
  };

  const handleTest = async () => {
    setStatus({ loading: true, msg: '', error: '' });
    try {
      const r = await testConnection();
      setStatus({ loading: false, msg: r.data?.message, error: r.data?.success ? '' : r.data?.message });
    } catch (err) {
      setStatus({ loading: false, msg: '', error: 'Test failed' });
    }
  };

  const handleSync = async () => {
    setStatus({ loading: true, msg: 'Syncing menu...', error: '' });
    try {
      const r = await syncMenu();
      setStatus({ loading: false, msg: `✅ Synced ${r.data?.synced} items`, error: '' });
      loadLogs();
    } catch (err) {
      setStatus({ loading: false, msg: '', error: 'Sync failed' });
    }
  };

  const handleCSV = async () => {
    if (!csvContent) return setStatus({ loading: false, msg: '', error: 'Paste CSV content first' });
    setStatus({ loading: true, msg: 'Importing CSV...', error: '' });
    try {
      const r = await syncCSV(csvContent);
      setStatus({ loading: false, msg: `✅ Imported ${r.data?.synced} items`, error: '' });
      setCsvContent('');
      loadLogs();
    } catch (err) {
      setStatus({ loading: false, msg: '', error: 'Import failed' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await deleteConnection();
      setConnection(null);
      setStep('overview');
    } catch (_) {}
  };

  const s = { card: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 } };

  // ── OVERVIEW ──
  if (step === 'overview') return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🔗 POS Integration</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>Connect your existing POS to sync menu and receive orders</div>
        </div>
        <button className="btn btn-primary" onClick={() => setStep('select')}>Connect POS</button>
      </div>
      <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--text2)' }}>
        <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>What POS integration does:</div>
        <div>📥 Pulls your menu automatically — no manual entry</div>
        <div style={{ marginTop: 4 }}>📤 Sends orders directly to your kitchen system</div>
        <div style={{ marginTop: 4 }}>🔄 Updates order status in real-time from your POS</div>
      </div>
    </div>
  );

  // ── SELECT POS TYPE ──
  if (step === 'select') return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button onClick={() => setStep('overview')} style={{ background: 'none', color: 'var(--text2)', fontSize: 18 }}>←</button>
        <div style={{ fontWeight: 700 }}>Select your POS system</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {types.map((type) => {
          const info = POS_LABELS[type] || { label: type, icon: '🔧', region: '' };
          return (
            <div key={type} onClick={() => { setSelected(type); setStep('configure'); }}
              style={{
                padding: 14, borderRadius: 10, border: '1px solid var(--border)',
                cursor: 'pointer', background: 'var(--bg3)',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'border .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <span style={{ fontSize: 24 }}>{info.icon}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{info.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{info.region}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── CONFIGURE ──
  if (step === 'configure') {
    const info    = POS_LABELS[selected] || { label: selected, icon: '🔧' };
    const isCSV   = selected === 'MANUAL_CSV';
    const needsId = ['SQUARE', 'LIGHTSPEED', 'PETPOOJA'].includes(selected);

    return (
      <div style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <button onClick={() => setStep('select')} style={{ background: 'none', color: 'var(--text2)', fontSize: 18 }}>←</button>
          <div style={{ fontWeight: 700 }}>{info.icon} Configure {info.label}</div>
        </div>

        {isCSV ? (
          <div>
            <div className="section-label" style={{ marginBottom: 8 }}>Paste CSV content</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
              Required columns: name, price — Optional: category, description, stock, active
            </div>
            <textarea
              value={csvContent}
              onChange={e => setCsvContent(e.target.value)}
              placeholder={`name,price,category,description\nShawarma Wrap,22,Wraps,Chicken and garlic sauce\nCheese Burger,28,Burgers,Double patty`}
              style={{
                width: '100%', height: 160, background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 12, color: 'var(--text)', fontSize: 12,
                fontFamily: 'monospace', resize: 'vertical',
              }}
            />
            {status.error && <div style={{ color: 'var(--red)', fontSize: 13, margin: '8px 0' }}>{status.error}</div>}
            {status.msg   && <div style={{ color: 'var(--green)', fontSize: 13, margin: '8px 0' }}>{status.msg}</div>}
            <button className="btn btn-primary" style={{ marginTop: 12, width: '100%', padding: 13 }}
              onClick={handleCSV} disabled={status.loading}>
              {status.loading ? 'Importing...' : '📥 Import Menu from CSV'}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div className="section-label" style={{ marginBottom: 6 }}>API Key *</div>
                <input type="password" placeholder="Your POS API key"
                  value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })} />
              </div>
              <div>
                <div className="section-label" style={{ marginBottom: 6 }}>API Secret</div>
                <input type="password" placeholder="API secret (if required)"
                  value={form.apiSecret} onChange={e => setForm({ ...form, apiSecret: e.target.value })} />
              </div>
              {selected === 'PETPOOJA' && (
                <div>
                  <div className="section-label" style={{ marginBottom: 6 }}>Access Token *</div>
                  <input type="password" placeholder="Pet Pooja access_token"
                    value={form.accessToken} onChange={e => setForm({ ...form, accessToken: e.target.value })} />
                </div>
              )}
              {selected === 'GENERIC_WEBHOOK' && (
                <div>
                  <div className="section-label" style={{ marginBottom: 6 }}>Your POS Endpoint URL</div>
                  <input type="text" placeholder="https://your-pos.com/api"
                    value={form.apiEndpoint} onChange={e => setForm({ ...form, apiEndpoint: e.target.value })} />
                </div>
              )}
              {needsId && (
                <div>
                  <div className="section-label" style={{ marginBottom: 6 }}>Location ID</div>
                  <input type="text" placeholder={selected === 'PETPOOJA' ? 'Pet Pooja restID' : 'Your location/branch ID'}
                    value={form.locationId} onChange={e => setForm({ ...form, locationId: e.target.value })} />
                </div>
              )}
              <div>
                <div className="section-label" style={{ marginBottom: 6 }}>Webhook Secret (optional)</div>
                <input type="password" placeholder="For verifying incoming webhooks"
                  value={form.webhookSecret} onChange={e => setForm({ ...form, webhookSecret: e.target.value })} />
              </div>
              <div>
                <div className="section-label" style={{ marginBottom: 6 }}>Sync Mode</div>
                <select value={form.syncMode} onChange={e => setForm({ ...form, syncMode: e.target.value })}>
                  <option value="WEBHOOK">Webhook — real-time push from POS</option>
                  <option value="API_PULL">API Pull — we fetch from POS on schedule</option>
                </select>
              </div>
            </div>
            {status.error && <div style={{ color: 'var(--red)', fontSize: 13, margin: '10px 0' }}>{status.error}</div>}
            {status.msg   && <div style={{ color: 'var(--green)', fontSize: 13, margin: '10px 0' }}>{status.msg}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" style={{ flex: 2, padding: 13 }}
                onClick={handleSave} disabled={status.loading}>
                {status.loading ? 'Saving...' : 'Save & Connect'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── CONNECTED ──
  if (step === 'connected' && connection) {
    const info = POS_LABELS[connection.posType] || { label: connection.posType, icon: '🔗' };
    return (
      <div style={s.card}>
        {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>{info.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{info.label} Connected</div>
              <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 2 }}>● Active</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={handleTest}>Test</button>
            <button className="btn btn-ghost btn-sm" onClick={handleSync}>Sync Menu Now</button>
            <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ message: 'Disconnect your POS? This will stop menu sync and order push.', confirmLabel: 'Disconnect', onConfirm: handleDisconnect })}>Disconnect</button>
          </div>
        </div>

        {status.msg   && <div style={{ color: 'var(--green)', fontSize: 13, padding: '8px 12px', background: '#1a3d2a', borderRadius: 8, marginBottom: 12 }}>{status.msg}</div>}
        {status.error && <div style={{ color: 'var(--red)',   fontSize: 13, padding: '8px 12px', background: '#3d1a1a', borderRadius: 8, marginBottom: 12 }}>{status.error}</div>}

        {/* Webhook URL */}
        {webhookUrl && (
          <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Your Webhook URL</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
              Configure this in your POS to receive real-time order status updates:
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ flex: 1, fontSize: 11, background: 'var(--bg4)', padding: '8px 10px', borderRadius: 6, color: 'var(--accent)', wordBreak: 'break-all' }}>
                {webhookUrl}
              </code>
              <button className="btn btn-ghost btn-sm"
                onClick={() => { navigator.clipboard.writeText(webhookUrl); setStatus({ loading: false, msg: 'Copied!', error: '' }); }}>
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Sync stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[
            ['Last Sync', connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString() : 'Never'],
            ['Sync Status', connection.lastSyncStatus || 'N/A'],
            ['Sync Mode', connection.syncMode],
          ].map(([label, val]) => (
            <div key={label} style={{ background: 'var(--bg3)', padding: 12, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Sync logs */}
        {logs.length > 0 && (
          <div>
            <div className="section-label" style={{ marginBottom: 8 }}>Recent Sync Log</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {logs.slice(0, 5).map((log) => (
                <div key={log.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8, fontSize: 12,
                }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span>{log.status === 'success' ? '✅' : '❌'}</span>
                    <span style={{ color: 'var(--text2)' }}>{log.type.replace('_', ' ')}</span>
                    {log.itemsSynced > 0 && <span style={{ color: 'var(--accent)' }}>{log.itemsSynced} items</span>}
                  </div>
                  <span style={{ color: 'var(--text3)' }}>
                    {new Date(log.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
